/**
 * Repeatability-policy evaluation (MUS-315).
 *
 * Pure functions - no lifecycle, no state. The counts they evaluate live
 * in the session machine's context (the engine owns state, #115); this
 * module only answers "does this verdict history satisfy this policy?"
 * after each take. Policy shapes are declared data on the exercise
 * (MUS-308 `ProgressPolicy`; #128 section 3.3).
 *
 * Evaluated incrementally (once per take), so `streak` and `x_of_y` read
 * the trailing window - the moment the bar is met is the take that met it.
 */

import type { ProgressPolicy, Verdict } from 'Types/ExerciseContentTypes';

/** A take qualifies toward clearance when it is clean or better. */
export const qualifiesForClearance = (verdict: Verdict): boolean =>
  verdict === 'clean' || verdict === 'flawless';

/** A take qualifies toward mastery only when flawless (mastery = flawless,
 *  not a percentage - #127). */
export const qualifiesForMastery = (verdict: Verdict): boolean => verdict === 'flawless';

/**
 * Whether `takes` (this sitting's verdict history for one (cell, config),
 * oldest first) satisfies `policy` under the given qualifying predicate.
 */
export function policyMet(
  takes: readonly Verdict[],
  policy: ProgressPolicy,
  qualifies: (verdict: Verdict) => boolean,
): boolean {
  switch (policy.type) {
    case 'single':
      return takes.some(qualifies);
    case 'cumulative':
      return takes.filter(qualifies).length >= policy.n;
    case 'streak':
      return takes.length >= policy.n && takes.slice(-policy.n).every(qualifies);
    case 'x_of_y':
      return takes.slice(-policy.m).filter(qualifies).length >= policy.n;
  }
}
